import { motion } from "framer-motion";
import "../CSS/HeroFB.css";

const container = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
      when: "beforeChildren",
      staggerChildren: 0.12,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

export default function HeroFB() {
  return (
    <section
      id="hero"
      className="relative flex min-h-[65vh] w-full items-center justify-center overflow-hidden bg-slate-950 text-white"
    >
      <video
        src="/assets/videos/video_hero.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40"
      />

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(129,140,248,0.2),_transparent_55%)]" />

      <div
        className="pointer-events-none absolute inset-0
        bg-gradient-to-b
        from-[#1a1535]/80 
        via-[#120f27]/85
        to-[#0a0915]/95"
      />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-indigo-500/70 to-transparent" />

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-20 top-20 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute right-[-12rem] bottom-10 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
      </div>

      <motion.div
        className="relative z-10 mx-auto flex w-full max-w-4xl flex-col items-center px-6 py-24 md:px-10"
        initial="hidden"
        animate="visible"
        variants={container}
      >
        <div className="inline-flex relative">
          <motion.div
            className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
            animate={{ opacity: [0.18, 0.4, 0.18], scale: [0.96, 1.03, 0.96] }}
            transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
          />

          <motion.p
            className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                       rounded-full text-[11px] uppercase tracking-[0.35em] 
                       bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shadow-[0_0_8px_#c4b5fd]"
              animate={{ opacity: [0.6, 1, 0.6], scale: [1, 1.25, 1] }}
              transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
            />
            New · 42,000+ downloads
            <motion.span
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(168,85,247,0.12), transparent)",
              }}
              animate={{ x: ["-160%", "160%"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.p>
        </div>

        <motion.h1
          className="
            mt-5 text-center text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl
            tracking-tight
            bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400
            bg-[length:300%_100%]
            bg-clip-text text-transparent
            drop-shadow-[0_0_10px_rgba(99,102,241,0.5)]
            animate-gradient-x
          "
          variants={item}
        >
          Float Block: Bloxorz Puzzle
        </motion.h1>

        <motion.p
          className="mt-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-400 sm:text-sm"
          variants={item}
        >
          Extreme 3D block puzzle challenge
        </motion.p>

        <motion.p
          className="mt-6 max-w-3xl text-center text-sm leading-relaxed text-slate-300 md:text-base lg:text-lg"
          variants={item}
        >
          <span className="font-semibold text-indigo-200">Float Block</span>{" "}
          is a 3D puzzle where precision is essential and time is your enemy
          <br></br>You have only{" "}
          <span className="text-indigo-200 font-semibold">60 seconds</span> to complete each level. Can you stay calm when every move matters?
        </motion.p>

        <motion.div
          className="mt-8 flex flex-col items-center gap-4 text-sm md:flex-row"
          variants={item}
        >
          <a
            href="https://play.google.com/store/apps/details?id=com.floatkeygames.floatblock"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 px-8 py-3 font-semibold text-white shadow-[0_20px_60px_rgba(79,70,229,0.55)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_25px_70px_rgba(79,70,229,0.75)]"
          >
            Play on Google Play
          </a>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-2xl border border-slate-500/70 bg-white/5 px-7 py-3 font-medium text-slate-100 backdrop-blur-md transition hover:border-indigo-300 hover:bg-slate-900/80 pointer-events-none"
          >
            iOS Coming Soon
          </button>
        </motion.div>

        <motion.div
          className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-slate-400 sm:text-sm"
          variants={item}
        >
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-400"/>
            42,000+ global downloads
          </div>

          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-400"/>
            Designed for logic & skill
          </div>

          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-400"/>
            Intense 60 second puzzles
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
